import { useLocalization } from '@components/Localization'
import { Box } from '@gassan-ui'
import { buttonLabel, Checkbox, Heading } from '@rolex-ui'
import { useRouter } from 'next/router'
import { rem } from 'polished'
import React, { Dispatch, FC } from 'react'
import styled from 'styled-components'
import filterOptions, { overviewPages } from './filterOptions'
import { FilterAction } from './reducer'
import { ResetButton } from './Sidebar/elements'

const FamilyLink = styled.a<{ active: boolean }>`
  ${buttonLabel}
  transition: color 0.2s ease-in-out;
  display: block;
  margin-bottom: 0.375rem;
  color: ${p => (p.active ? p.theme.rolex.colors.primary : p.theme.rolex.colors.shades[900])};

  &:hover,
  &:focus {
    color: ${p => p.theme.rolex.colors.primary};
  }
`

interface Props {
  dispatch: Dispatch<FilterAction>
  style: string[]
  size: string[]
  material: string[]
  dial: string[]
  fid: string
  disableReset: boolean
}

const Filters: FC<Props> = ({ dispatch, style, size, material, dial, fid, disableReset }) => {
  const filters = filterOptions[fid]
  const router = useRouter()
  const { messages, lang } = useLocalization()
  const families = Object.keys(filterOptions).filter(t => !overviewPages.includes(t))
  let displayResetFilters = false
  if (filters.styles || filters.sizes || filters.materials || filters.dials) {
    displayResetFilters = true
  }

  return (
    <>
      {displayResetFilters && (
        <Box mb={rem(50)} display={{ _: 'none', large: 'block' }}>
          <Heading variant="pushTitle" as="h3" mb={rem(20)}>
            {messages.rolex.filters.filterYour}
          </Heading>
          <ResetButton
            icon="rolex-refresh-arrow"
            iconPosition="left"
            variant="secondary"
            disabled={disableReset}
            onClick={() => dispatch({ type: 'reset', fixed: filters.fixed })}
          >
            Reset filter
          </ResetButton>
        </Box>
      )}
      <Box display={{ _: 'contents', small: 'block', large: 'contents' }} gridColumn={1}>
        {filters.styles && (
          <Box mb={rem(50)}>
            <Heading variant="smallTitle">{messages.rolex.filters.style}</Heading>
            {filters.styles.map(key => (
              <Checkbox
                key={key}
                label={messages.rolex.filters[`style_${key}`]}
                checked={style.includes(key)}
                onChange={() => dispatch({ type: 'toggle-style', style: key })}
              />
            ))}
          </Box>
        )}
        {filters.sizes && (
          <Box mb={rem(50)}>
            <Heading variant="smallTitle">{messages.rolex.filters.size}</Heading>
            {filters.sizes.map(key => (
              <Checkbox
                key={key}
                label={messages.rolex.filters[`size_${key}`]}
                checked={size.includes(key)}
                onChange={() => dispatch({ type: 'toggle-size', size: key })}
              />
            ))}
          </Box>
        )}
        {filters.materials && (
          <Box mb={rem(50)}>
            <Heading variant="smallTitle">{messages.rolex.filters.material}</Heading>
            {filters.materials.map(key => (
              <Checkbox
                key={key}
                label={messages.rolex.filters[`material_${key}`]}
                checked={material.includes(key)}
                onChange={() => dispatch({ type: 'toggle-material', material: key })}
              />
            ))}
          </Box>
        )}
        {filters.dials && (
          <Box mb={rem(50)}>
            <Heading variant="smallTitle">{messages.rolex.filters.dial}</Heading>
            {filters.dials.map(key => (
              <Checkbox
                key={key}
                label={messages.rolex.filters[`dial_${key}`]}
                checked={dial.includes(key)}
                onChange={() => dispatch({ type: 'toggle-dial', dial: key })}
              />
            ))}
          </Box>
        )}
      </Box>
      <Box gridColumn={2} gridRow={2}>
        <Heading variant="smallTitle">Families</Heading>
        {families.map(key => (
          <FamilyLink
            href={`/${lang}/rolex/${key}`}
            active={router.query.fid === key}
            key={`/rolex/${key}`}
          >
            {`Rolex ${filterOptions[key].title}`}
          </FamilyLink>
        ))}
      </Box>
    </>
  )
}

export default Filters
